// FE-12 — preparar la foto del recibo ANTES de subirla.
//
// El core acepta como mucho 10 MiB y calcula el SHA-256 de lo que recibe. Una
// foto de un teléfono moderno son 3-8 MB, y subirla entera desde la carretera
// con señal mala tarda minutos o falla. Por decisión del dueño (2026-09-11) la
// imagen se **comprime en el cliente** antes de salir.
//
// ⚠️ **Consecuencia asumida, no un descuido:** el `integrityHash` que guarda el
// core es el del JPEG comprimido, no el del archivo original de la cámara —que
// no se conserva en ninguna parte—. Lo que el hash garantiza es que el recibo
// que se descarga es byte a byte el que se subió; nunca prometió ser el
// negativo de la foto.

/** Tope del core (`console.MaxReciboBytes`). Se comprueba aquí para no gastar la subida en un 400. */
export const MAX_RECIBO_BYTES = 10 * 1024 * 1024;

/** Lado mayor del JPEG resultante. A 1600 px un recibo de papel se lee sin esfuerzo. */
const LADO_MAYOR = 1600;

const CALIDAD_JPEG = 0.8;

/**
 * Lo que `ValidarRecibo` acepta en el backend (`console/expenses.go`,
 * `tiposDeRecibo`). La lista es **exactamente** esa, ni uno más.
 *
 * ⚠️ **`image/heif` NO está**, aunque `image/heic` sí. No es un olvido del
 * backend: son dos tipos MIME distintos y el core solo declara el que mandan
 * las cámaras de iPhone. Tenerlo aquí de más no lo hacía funcionar — hacía que
 * el cliente gastara la subida entera para cobrar un 400.
 */
export const TIPOS_ACEPTADOS = ['image/jpeg', 'image/png', 'image/heic', 'image/webp', 'application/pdf'];

export class ReciboDemasiadoGrande extends Error {
  constructor(bytes: number) {
    super(`El archivo pesa ${(bytes / (1024 * 1024)).toFixed(1)} MB y el tope son 10 MB.`);
    this.name = 'ReciboDemasiadoGrande';
  }
}

/**
 * Deja el archivo listo para `POST /expenses/{id}/receipts`.
 *
 * - **PDF pasa sin tocar.** Comprimir un PDF exigiría una librería entera y un
 *   soporte contable escaneado ya viene del tamaño que viene. Si supera el tope
 *   se rechaza **aquí**, con el motivo, en vez de gastar la subida para que el
 *   servidor conteste 400.
 * - **Las imágenes se redibujan** a JPEG con el lado mayor en 1600 px. Una foto
 *   que ya sea más pequeña no se agranda: solo se recodifica si eso la hace
 *   pesar menos.
 * - **Si el navegador no puede decodificar el formato** (un HEIC fuera de iOS,
 *   por ejemplo) se sube el original. Fallar la captura entera por no poder
 *   comprimir sería peor que subir 4 MB: el core acepta HEIC.
 *
 * ⚠️ El `File` que devuelve lleva `type` explícito **siempre**. El backend lee
 * el Content-Type de la PARTE del multipart, que sale de ese campo; un blob sin
 * tipo llega con la cabecera vacía y responde 400.
 *
 * ⚠️ **Es idempotente**: llamarla con un archivo que ya salió de aquí lo
 * devuelve tal cual. Los dos caminos que suben pueden invocarla sin coordinarse
 * y sin recodificar dos veces — ver `YA_PREPARADOS`.
 */
export async function comprimirRecibo(file: File): Promise<File> {
  if (YA_PREPARADOS.has(file)) return file;
  const listo = await prepararRecibo(file);
  YA_PREPARADOS.add(listo);
  return listo;
}

/**
 * Los archivos que ya salieron de `comprimirRecibo`.
 *
 * ⚠️ **Sin esto el recibo se comprimía DOS veces** y nadie lo veía: el selector
 * prepara el archivo al elegirlo —necesita el resultado para enseñar cuánto va
 * a pesar— y quien sube volvía a llamar. La guarda de "no recodificar" de abajo
 * no lo frenaba, porque recodificar un JPEG q=0.8 a q=0.8 sí produce un blob
 * más pequeño: la segunda pasada pasaba el filtro y devolvía otra generación
 * perdida. En un documento cuyo valor entero es que se lean las cifras, eso no
 * es gratis.
 *
 * Es un `WeakSet` y no una marca en el archivo porque un `File` es inmutable de
 * cara al `FormData`, y porque así los archivos que el usuario descarta se
 * recogen solos.
 */
const YA_PREPARADOS = new WeakSet<File>();

async function prepararRecibo(file: File): Promise<File> {
  if (file.type === 'application/pdf') {
    if (file.size > MAX_RECIBO_BYTES) throw new ReciboDemasiadoGrande(file.size);
    return file;
  }

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    // El navegador no sabe decodificarlo. Se manda tal cual y decide el core.
    if (file.size > MAX_RECIBO_BYTES) throw new ReciboDemasiadoGrande(file.size);
    return conTipo(file);
  }

  try {
    const escala = Math.min(1, LADO_MAYOR / Math.max(bitmap.width, bitmap.height));
    const ancho = Math.max(1, Math.round(bitmap.width * escala));
    const alto = Math.max(1, Math.round(bitmap.height * escala));

    const canvas = document.createElement('canvas');
    canvas.width = ancho;
    canvas.height = alto;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      if (file.size > MAX_RECIBO_BYTES) throw new ReciboDemasiadoGrande(file.size);
      return conTipo(file);
    }
    ctx.drawImage(bitmap, 0, 0, ancho, alto);

    const blob = await new Promise<Blob | null>(resolve =>
      canvas.toBlob(resolve, 'image/jpeg', CALIDAD_JPEG),
    );
    if (!blob) {
      if (file.size > MAX_RECIBO_BYTES) throw new ReciboDemasiadoGrande(file.size);
      return conTipo(file);
    }

    // Si recodificar no ganó nada —un JPEG ya pequeño y bien comprimido— se
    // conserva el original: reescribirlo solo cambiaría el hash sin beneficio.
    if (blob.size >= file.size && escala === 1 && file.size <= MAX_RECIBO_BYTES) {
      return conTipo(file);
    }
    if (blob.size > MAX_RECIBO_BYTES) throw new ReciboDemasiadoGrande(blob.size);

    return new File([blob], nombreJpeg(file.name), { type: 'image/jpeg', lastModified: Date.now() });
  } finally {
    bitmap.close();
  }
}

/**
 * Garantiza el `type` del archivo que se sube. Algunos navegadores entregan un
 * `File` con `type` vacío (un HEIC de iOS al elegirlo de la galería), y sin él
 * la parte del multipart llega sin Content-Type y el core responde 400.
 *
 * ⚠️ **Se infiere por EXTENSIÓN, no por defecto a JPEG.** El caso que motiva
 * esta función es justamente el HEIC, y etiquetarlo `image/jpeg` no lo
 * convierte: sube bytes HEIC bajo una cabecera que miente. El core no husmea
 * el contenido —confía en el Content-Type de la parte (`httpapi/expenses.go`,
 * `leerRecibo`)—, así que el recibo queda registrado con un tipo falso y el
 * visor pinta una imagen rota que nadie sabe por qué no carga.
 */
const TIPO_POR_EXTENSION: Array<[RegExp, string]> = [
  [/\.pdf$/i, 'application/pdf'],
  [/\.hei[cf]$/i, 'image/heic'],
  [/\.png$/i, 'image/png'],
  [/\.webp$/i, 'image/webp'],
];

function conTipo(file: File): File {
  if (file.type) return file;
  const inferido = TIPO_POR_EXTENSION.find(([re]) => re.test(file.name))?.[1] ?? 'image/jpeg';
  return new File([file], file.name, { type: inferido, lastModified: file.lastModified });
}

function nombreJpeg(nombre: string): string {
  const base = nombre.replace(/\.[^.]+$/, '') || 'recibo';
  return `${base}.jpg`;
}

/** `true` si el archivo elegido es de un tipo que el core acepta. */
export function tipoAceptado(file: File): boolean {
  if (TIPOS_ACEPTADOS.includes(file.type)) return true;
  // `type` vacío: se decide por la extensión, igual que `conTipo` —que además
  // mapea `.heif` a `image/heic`, el único de los dos que el core acepta.
  return file.type === '' && /\.(jpe?g|png|hei[cf]|webp|pdf)$/i.test(file.name);
}
